import { Calendar03Icon, ChevronDownIcon } from "@/icons";
import { formatDate } from "@/utils";
import { Divider, Flex, Switch } from "antd";
import { getRecommendationSections } from "./recommendation.constants";
// ──────── Types ────────
interface ManagerRecommendation {
  recommendationType?: string | null;
  lastWorkingDay?: string | null;
  incentiveEffectiveMonth?: string | null;
  incentiveType?: string | null;
  incentiveValue?: number | string | null;
  incentiveBase?: string | null;
  suggestedDesignationId?: string | null;
  newSalary?: number | null;
  promotionEffectiveMonth?: string | null;
  incrementEffectiveMonth?: string | null;
  incrementType?: string | null;
  incrementValue?: number | string | null;
  incrementBase?: string | null;
}

interface ManagerMapping {
  id?: string;
  manager?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  managerRecommendation?: ManagerRecommendation | null;
}

interface ManagerRecommendationsProps {
  managerMappings: ManagerMapping[];
  designationMap?: Record<string, string>;
}

// ──────── Recommendation Sections Config ────────
const RECOMMENDATION_SECTIONS = [
  { key: "DISMISSAL", label: "Dismissal" },
  { key: "INCENTIVE", label: "Incentive" },
  { key: "PROMOTION", label: "Promotion" },
  { key: "INCREMENT", label: "Increment" },
] as const;

// ──────── Sub-components ────────

/** Read-only benefit display for Incentive / Increment */
const BenefitDisplay = ({
  type,
  value,
  base,
}: {
  type?: string | null;
  value?: number | string | null;
  base?: string | null;
}) => {
  const isPercentage = type === "PERCENTAGE";

  return (
    <div className="space-y-2">
      <Flex justify="space-between" align="center" className="w-full">
        <span className="text-white text-sm font-medium">Benefit Setup</span>
        <div className="flex border border-theme-slate-700 rounded bg-theme-slate-800 overflow-hidden p-[2px]">
          <span
            className={`px-4 py-1 text-xs rounded transition-all select-none ${
              !isPercentage
                ? "bg-white text-slate-950 font-bold"
                : "text-white/60"
            }`}
          >
            Flat
          </span>
          <span
            className={`px-4 py-1 text-xs rounded transition-all select-none ${
              isPercentage
                ? "bg-white text-slate-950 font-bold"
                : "text-white/60"
            }`}
          >
            %
          </span>
        </div>
      </Flex>

      {isPercentage ? (
        <div className="flex w-full items-center border border-theme-slate-700 rounded bg-theme-slate-800 overflow-hidden pr-1 mt-2 min-h-[44px] justify-between">
          <div className="px-4 text-white text-sm font-medium flex-1">
            {value || "0"}%
          </div>
          <div className="border-l border-theme-slate-700 px-4 text-white text-sm font-medium capitalize min-w-[160px] min-h-[44px] flex justify-between items-center bg-theme-slate-800/50">
            <span>{base?.toLowerCase() || "--"}</span>
            <ChevronDownIcon size={16} className="text-white opacity-80" />
          </div>
        </div>
      ) : (
        <div className="mt-2 border border-theme-slate-700 rounded bg-theme-slate-800 px-4 py-2.5 text-white text-sm min-h-[44px] flex items-center">
          <span className="text-white/50 mr-2 text-xs">BDT</span>
          <span className="font-semibold text-white">{value || "0"}</span>
        </div>
      )}
    </div>
  );
};

/** Read-only date field display */
const DateDisplay = ({ label, value }: { label: string; value?: string }) => (
  <div>
    <label className="text-theme-gray-400 text-xs mb-1.5 block font-semibold tracking-wider">
      {label}
    </label>
    <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-2.5 text-white text-sm min-h-[44px] flex justify-between items-center">
      <span>{value || "--"}</span>
      <Calendar03Icon size={20} className="text-white opacity-80" />
    </div>
  </div>
);

/** Read-only text field display */
const FieldDisplay = ({
  label,
  value,
  suffix,
}: {
  label: string;
  value?: string | number | null;
  suffix?: React.ReactNode;
}) => (
  <div>
    <label className="text-theme-gray-400 text-xs mb-1.5 block font-semibold tracking-wider">
      {label}
    </label>
    <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-2.5 text-white text-sm min-h-[44px] flex justify-between items-center">
      <span className="font-semibold text-white">
        {value != null ? String(value) : "--"}
      </span>
      {suffix}
    </div>
  </div>
);

// ──────── Recommendation Section Renderers ────────

const DismissalSection = ({ mr }: { mr: ManagerRecommendation }) => (
  <div className="grid grid-cols-2 gap-6">
    <DateDisplay
      label="Last Working Day"
      value={
        mr.lastWorkingDay
          ? formatDate(mr.lastWorkingDay, "DD/MM/YYYY")
          : undefined
      }
    />
  </div>
);

const IncentiveSection = ({ mr }: { mr: ManagerRecommendation }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-6">
      <DateDisplay
        label="Effective Month"
        value={
          mr.incentiveEffectiveMonth
            ? formatDate(mr.incentiveEffectiveMonth, "MMMM YYYY")
            : undefined
        }
      />
    </div>
    <BenefitDisplay
      type={mr.incentiveType}
      value={mr.incentiveValue}
      base={mr.incentiveBase}
    />
  </div>
);

const PromotionSection = ({
  mr,
  designationMap,
}: {
  mr: ManagerRecommendation;
  designationMap?: Record<string, string>;
}) => (
  <div className="space-y-4">
    <FieldDisplay
      label="Suggested Designation"
      value={
        designationMap?.[mr.suggestedDesignationId || ""] || "Loading..."
      }
      suffix={
        <ChevronDownIcon size={20} className="text-white opacity-80" />
      }
    />
    <div className="grid grid-cols-2 gap-6">
      <FieldDisplay label="New Salary" value={mr.newSalary} />
      <DateDisplay
        label="Effective Month"
        value={
          mr.promotionEffectiveMonth
            ? formatDate(mr.promotionEffectiveMonth, "MMMM YYYY")
            : undefined
        }
      />
    </div>
  </div>
);

const IncrementSection = ({ mr }: { mr: ManagerRecommendation }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-6">
      <DateDisplay
        label="Effective Month"
        value={
          mr.incrementEffectiveMonth
            ? formatDate(mr.incrementEffectiveMonth, "MMMM YYYY")
            : undefined
        }
      />
    </div>
    <BenefitDisplay
      type={mr.incrementType}
      value={mr.incrementValue}
      base={mr.incrementBase}
    />
  </div>
);

// ──────── Section Map ────────
const SECTION_RENDERERS: Record<
  string,
  (
    mr: ManagerRecommendation,
    designationMap?: Record<string, string>,
  ) => React.ReactNode
> = {
  DISMISSAL: (mr) => <DismissalSection mr={mr} />,
  INCENTIVE: (mr) => <IncentiveSection mr={mr} />,
  PROMOTION: (mr, designationMap) => (
    <PromotionSection mr={mr} designationMap={designationMap} />
  ),
  INCREMENT: (mr) => <IncrementSection mr={mr} />,
};

// ──────── Main Component ────────
const ManagerRecommendations = ({
  managerMappings,
  designationMap,
}: ManagerRecommendationsProps) => {
  const hasRecommendations = managerMappings.some(
    (m) => m.managerRecommendation,
  );

  if (!hasRecommendations) return null;

  return (
    <div className="border border-white/30 rounded-xl p-6 space-y-6">
      <h4 className="text-white text-base font-semibold">
        Manager Recommendations
      </h4>
      <div className="space-y-6">
        {managerMappings.map((mapping, mIdx) => {
          const mr = mapping.managerRecommendation;
          if (!mr) return null;

          const managerName = mapping.manager
            ? `${mapping.manager.firstName || ""} ${mapping.manager.lastName || ""}`.trim()
            : "";

          return (
            <div key={mapping.id || mIdx} className="space-y-4">
              {mIdx > 0 && (
                <div className="border-t border-dashed border-white/30 pt-2" />
              )}
              <h5 className="text-white text-sm font-medium">
                Line Manager - {mIdx + 1} Recommendation{" "}
                {managerName ? `(${managerName})` : ""}
              </h5>
              <div className="border border-white/30 rounded-xl p-6 space-y-0">
                {RECOMMENDATION_SECTIONS.map((section) => {
                  const activeSections = getRecommendationSections(
                    mr.recommendationType,
                  );
                  const isActive = activeSections.includes(section.key);
                  if (!isActive) return null;

                  return (
                    <div key={section.key}>
                      <Flex justify="space-between" align="center">
                        <h5 className="text-base font-medium text-white">
                          {section.label}
                        </h5>
                        <Switch
                          className="custom-switch"
                          checked={isActive}
                          disabled
                        />
                      </Flex>
                      <Divider className="border-dashed my-3" />
                      {SECTION_RENDERERS[section.key]?.(mr, designationMap)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ManagerRecommendations;
